import {
	faArrowRightFromBracket,
	faArrowRightToBracket,
	faBell,
	faBoxOpen,
	faBuilding,
	faClipboardList,
	faEnvelope,
	faFileInvoiceDollar,
	faFileLines,
	faHouse,
	faLocationDot,
	faMobileScreenButton,
	faMoon,
	faPhone,
	faReceipt,
	faSunBright,
	faTimes,
	faUser,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { PortalClientDto } from '@supply-mover-2-0/shared';
import { useTheme, useTranslation } from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDisplaySettings } from '../hooks/useDisplaySettings';
import { useMobileAppPromotion } from '../hooks/useMobileAppPromotion';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { clientDisplayId } from '../lib/clientDisplayId';
import { telHref } from '../lib/supportContact';
import { useCartStore } from '../stores/cartStore';
import { useLoginModalStore } from '../stores/loginModalStore';
import { useMenuDrawerStore } from '../stores/menuDrawerStore';
import { useNotificationTrayStore } from '../stores/notificationTrayStore';
import { usePortalSiteStore } from '../stores/portalSiteStore';
import { LanguageSwitcher } from './layout/LanguageSwitcher';
import { usePortalUnreadCount } from './PortalNotificationBell';

function formatClientAddress(client: PortalClientDto): string | null {
	const parts: Array<string> = [];
	if (client.address1 != null && client.address1.length > 0) {
		parts.push(client.address1);
	}
	if (client.address2 != null && client.address2.length > 0) {
		parts.push(client.address2);
	}
	const cityStateZip: Array<string> = [];
	if (client.city != null && client.city.length > 0) {
		cityStateZip.push(client.city);
	}
	if (client.state != null && client.state.length > 0) {
		cityStateZip.push(client.state);
	}
	let locationPart = cityStateZip.join(', ');
	if (client.zip != null && client.zip.length > 0) {
		locationPart =
			locationPart.length > 0 ? `${locationPart} ${client.zip}` : client.zip;
	}
	if (locationPart.length > 0) {
		parts.push(locationPart);
	}
	return parts.length > 0 ? parts.join(', ') : null;
}

/**
 * Mobile menu drawer triggered by the BottomNav's "Menu" tab.
 *
 * Replaces the desktop UserMenu dropdown on mobile — same content, but
 * presented as a right-side slide-in panel since dropdowns are awkward
 * inside a sticky brand header on small screens. Holds:
 *   - User identity (or Sign In CTA when anon)
 *   - Active client / Change Location (when multi-client)
 *   - Shop / Orders / Invoices / Account (gated by storefront features)
 *   - Language switcher (only renders when multi-language is configured)
 *   - Theme toggle
 *   - Sign out
 *
 * Closes on:
 *   - Backdrop tap
 *   - Close button in the header
 *   - Any nav action inside the drawer
 *   - Escape key
 *   - Browser back button (route change)
 */
export function MenuDrawer(): JSX.Element | null {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const isOpen = useMenuDrawerStore((s) => s.isOpen);
	const close = useMenuDrawerStore((s) => s.close);
	const { isAuthenticated, profile, activeClient, availableClients, logout } =
		usePortalAuth();
	const display = useDisplaySettings();
	const appPromotion = useMobileAppPromotion();
	const { isDark, toggleTheme } = useTheme();
	const openLoginModal = useLoginModalStore((s) => s.open);
	const setCart = useCartStore((s) => s.setCart);
	// Customer-support contact (PD-14363). The drawer is the mobile
	// counterpart of the desktop header's inline helpline, with room for
	// the full contact set (phone + email + hours).
	const supportContact = usePortalSiteStore(
		(s) => s.site?.supportContact ?? null,
	);
	const salesContact = usePortalSiteStore((s) => s.site?.salesContact ?? null);
	// PD-16475 — the phone's way into the notifications tray (the desktop
	// header has a bell). Counted from the bell's shared poll.
	const unreadNotifications = usePortalUnreadCount(isAuthenticated);
	const openNotificationTray = useNotificationTrayStore((s) => s.open);

	// Close on Escape so keyboard users / Capacitor hardware-back can
	// dismiss without hunting for the close button.
	useEffect(() => {
		if (!isOpen) {
			return;
		}
		const onKey = (e: KeyboardEvent): void => {
			if (e.key === 'Escape') {
				close();
			}
		};
		document.addEventListener('keydown', onKey);
		return () => {
			document.removeEventListener('keydown', onKey);
		};
	}, [isOpen, close]);

	// Close whenever the route changes underneath the drawer — browser
	// back/forward and the bottom-nav tabs all navigate while the drawer
	// is open, and without this the new page renders hidden behind it.
	// (The bottom nav also closes eagerly on tap so same-route taps
	// dismiss too; this catches every other navigation source.)
	const { pathname } = useLocation();
	useEffect(() => {
		close();
	}, [pathname, close]);

	if (!isOpen) {
		return null;
	}

	const go = (path: string): void => {
		close();
		navigate(path);
	};

	const handleSignIn = (): void => {
		close();
		openLoginModal();
	};

	const handleSignOut = (): void => {
		close();
		logout();
		setCart(null);
		navigate('/');
		openLoginModal();
	};

	const initials =
		profile != null
			? `${profile.firstName?.charAt(0) ?? ''}${profile.lastName?.charAt(0) ?? ''}`.toUpperCase() ||
				'?'
			: '?';
	const activeAddress =
		activeClient != null ? formatClientAddress(activeClient) : null;
	const hasMultipleClients = availableClients.length > 1;

	return (
		// PD-14705: the drawer is a bottom sheet that emerges from the Menu
		// tab and anchors ABOVE the bottom nav (which stays visible at z-50),
		// so the same Menu tab toggles it closed. Mobile-only (`md:hidden`).
		// The dim backdrop above the sheet is a secondary tap-to-close target;
		// the header X is the tertiary one. `bottom` stops at the nav's
		// footprint (≈48px tab row + the iOS home-indicator inset) so the nav
		// is never overlapped.
		<>
			<button
				type="button"
				aria-label={t('Close menu')}
				onClick={close}
				className="md:hidden fixed inset-0 z-40 bg-black/40 drawer-backdrop-in"
			/>
			<aside
				className="md:hidden fixed inset-x-0 top-16 z-40 rounded-t-2xl bg-brand-surface shadow-2xl flex flex-col overflow-hidden drawer-slide-up"
				// 36px = the 48px tab row minus the 12px the nav now overlaps
				// into the home-indicator inset (BottomNav's trimmed padding);
				// keeps the sheet flush against the nav's top edge.
				style={{
					bottom: 'max(calc(36px + env(safe-area-inset-bottom)), 48px)',
				}}
				role="dialog"
				aria-modal="true"
				aria-label={t('Menu')}
			>
				{/* Grabber + header strip — the X is the secondary close
				    (the Menu tab toggle is primary). */}
				<div className="flex items-center justify-between px-4 h-12 border-b border-subtle shrink-0">
					<span className="text-base font-semibold text-default">
						{t('Menu')}
					</span>
					<button
						type="button"
						onClick={close}
						className="w-10 h-10 -mr-2 flex items-center justify-center text-brand-muted rounded-lg hover:bg-sunken"
						aria-label={t('Close menu')}
					>
						<FontAwesomeIcon icon={faTimes} />
					</button>
				</div>

				{/* Scrollable body — identity, optional client picker, links */}
				<div className="flex-1 overflow-y-auto">
					{/* Identity — auth state determines presentation */}
					{isAuthenticated && profile != null ? (
						<div className="px-4 py-3 border-b border-subtle">
							<div className="flex items-center gap-3">
								<div className="w-10 h-10 rounded-full bg-portal-accent text-white flex items-center justify-center text-sm font-semibold shrink-0">
									{initials}
								</div>
								<div className="min-w-0 flex-1">
									<div className="text-sm font-medium text-default truncate">
										{t(profile.firstName ?? '')} {t(profile.lastName ?? '')}
									</div>
									<div className="text-xs text-brand-muted truncate">
										{t(profile.email ?? '')}
									</div>
								</div>
							</div>
						</div>
					) : (
						<div className="px-4 py-3 border-b border-subtle">
							<button
								type="button"
								onClick={handleSignIn}
								className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-portal-accent hover:bg-portal-accent-hover text-white font-semibold text-sm transition-colors min-h-touch"
							>
								<FontAwesomeIcon icon={faArrowRightToBracket} />
								<span>{t('Sign In')}</span>
							</button>
						</div>
					)}

					{/* Active client + Change Location — only when authed */}
					{isAuthenticated && activeClient != null && (
						<div className="border-b border-subtle px-4 py-3">
							<div className="text-[10px] font-semibold text-brand-muted uppercase tracking-wider mb-1">
								{t('Location')}
							</div>
							<div className="flex items-start gap-2">
								<FontAwesomeIcon
									icon={faBuilding}
									className="text-xs text-faint mt-1 shrink-0"
								/>
								<div className="flex-1 min-w-0">
									<div className="text-sm font-medium text-default truncate">
										{t(activeClient.name ?? '')}
									</div>
									<div className="text-xs text-brand-muted mt-0.5">
										{t('ID')}: {t(clientDisplayId(activeClient))}
									</div>
									{activeAddress != null && (
										<div className="text-xs text-brand-muted mt-0.5">
											{t(activeAddress)}
										</div>
									)}
								</div>
							</div>
							{hasMultipleClients && (
								<button
									type="button"
									onClick={() => {
										go('/select-client');
									}}
									className="mt-2 inline-flex items-center gap-1.5 text-xs text-portal-accent hover:underline"
								>
									<FontAwesomeIcon
										icon={faLocationDot}
										className="text-[10px]"
									/>
									<span>{t('Change Location')}</span>
								</button>
							)}
						</div>
					)}

					{/* Primary nav — Shop / Orders / Invoices / Account, gated
					    by feature flags so storefronts that hide order history
					    or purchasing don't expose dead links. */}
					<nav className="py-1">
						{/* PD-14704: Home leads the drawer nav when the portal uses a
					    home surface (the home tab is in its bottom-nav config), so
					    the menu is a complete map of the app. */}
						{display.mobileNavTabs.includes('home') && (
							<DrawerLink
								icon={faHouse}
								label={t('Home')}
								onClick={() => {
									go('/');
								}}
							/>
						)}
						{display.canBrowseCatalog && (
							<DrawerLink
								icon={faBoxOpen}
								label={t('Shop')}
								onClick={() => {
									go('/catalog');
								}}
							/>
						)}
						{isAuthenticated && display.canViewOrderHistory && (
							<DrawerLink
								icon={faClipboardList}
								label={t('Orders')}
								onClick={() => {
									go('/orders');
								}}
							/>
						)}
						{isAuthenticated && display.canViewInvoices && (
							<DrawerLink
								icon={faFileInvoiceDollar}
								label={t('Invoices')}
								onClick={() => {
									go('/invoices');
								}}
							/>
						)}
						{isAuthenticated && display.canViewStatements && (
							<DrawerLink
								icon={faFileLines}
								label={t('Statements')}
								onClick={() => {
									go('/statements');
								}}
							/>
						)}
						{isAuthenticated && display.canViewTransactions && (
							<DrawerLink
								icon={faReceipt}
								label={t('Transactions')}
								onClick={() => {
									go('/transactions');
								}}
							/>
						)}
						{isAuthenticated && (
							<DrawerLink
								icon={faBell}
								label={t('Notifications')}
								badge={unreadNotifications}
								onClick={() => {
									close();
									openNotificationTray();
								}}
							/>
						)}
						{isAuthenticated && (
							<DrawerLink
								icon={faUser}
								label={t('Account Details')}
								onClick={() => {
									go('/account');
								}}
							/>
						)}
						{/* PD-16558 — stays available after the phone's Get
						    the app bar is closed. Hidden inside the app. */}
						{appPromotion != null && (
							<DrawerLink
								icon={faMobileScreenButton}
								label={t('Get the app')}
								onClick={() => {
									go('/app');
								}}
							/>
						)}
					</nav>

					{/* Settings — language switcher only renders when multiple
					    active languages exist (component self-hides), theme
					    toggle is always relevant. */}
					<div className="border-t border-subtle py-1">
						{/* LanguageSwitcher renders its own dropdown trigger
						    when applicable, otherwise returns null. Wrap so
						    the drawer styling lines up. */}
						<div className="px-2">
							<LanguageSwitcher />
						</div>
						<DrawerLink
							icon={isDark ? faSunBright : faMoon}
							label={isDark ? t('Light Mode') : t('Dark Mode')}
							onClick={toggleTheme}
						/>
					</div>

					{/* General sales contact (PD-15552) — sits above customer
					    support, mirroring the Get Help card's order. Hidden when
					    the brand has not configured one. */}
					{salesContact != null && (
						<div className="border-t border-subtle py-3">
							<div className="px-4 pb-1 text-xs font-semibold text-brand-muted uppercase tracking-wider">
								{t('Sales')}
							</div>
							{salesContact.phone != null && (
								<a
									href={telHref(salesContact.phone)}
									className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-neutral-tint-fg hover:bg-brand-input min-h-touch"
								>
									<FontAwesomeIcon
										icon={faPhone}
										className="text-base w-5 text-center text-brand-muted"
									/>
									<span>{t(salesContact.phone)}</span>
								</a>
							)}
							{salesContact.email != null && (
								<a
									href={`mailto:${salesContact.email}`}
									className="flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-tint-fg hover:bg-brand-input min-h-touch"
								>
									<FontAwesomeIcon
										icon={faEnvelope}
										className="text-base w-5 text-center text-brand-muted"
									/>
									<span className="truncate">{t(salesContact.email)}</span>
								</a>
							)}
						</div>
					)}

					{/* Customer support (PD-14363) — the mobile home of the
					    helpline (the desktop header shows it inline instead).
					    Real tel:/mailto: links so a tap dials or drafts. */}
					{supportContact != null && (
						<div className="border-t border-subtle py-3">
							<div className="px-4 pb-1 text-xs font-semibold text-brand-muted uppercase tracking-wider">
								{t('Customer Support')}
							</div>
							{supportContact.phone != null && (
								<a
									href={telHref(supportContact.phone)}
									className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-neutral-tint-fg hover:bg-brand-input min-h-touch"
								>
									<FontAwesomeIcon
										icon={faPhone}
										className="text-base w-5 text-center text-brand-muted"
									/>
									<span>{t(supportContact.phone)}</span>
								</a>
							)}
							{supportContact.email != null && (
								<a
									href={`mailto:${supportContact.email}`}
									className="flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-tint-fg hover:bg-brand-input min-h-touch"
								>
									<FontAwesomeIcon
										icon={faEnvelope}
										className="text-base w-5 text-center text-brand-muted"
									/>
									<span className="truncate">{t(supportContact.email)}</span>
								</a>
							)}
							{supportContact.businessHours != null && (
								<div className="px-4 pt-1 text-xs text-brand-muted">
									{t(supportContact.businessHours)}
								</div>
							)}
						</div>
					)}

					{/* Sign out — separate section so it doesn't sit next to
					    everyday nav and get tapped accidentally. */}
					{isAuthenticated && (
						<div className="border-t border-subtle py-1">
							<DrawerLink
								icon={faArrowRightFromBracket}
								label={t('Sign Out')}
								onClick={handleSignOut}
							/>
						</div>
					)}
				</div>
			</aside>
		</>
	);
}

interface DrawerLinkProps {
	icon: typeof faUser;
	label: string;
	onClick: () => void;
	/** A count shown at the end of the row when above zero. */
	badge?: number;
}

function DrawerLink({
	icon,
	label,
	onClick,
	badge,
}: DrawerLinkProps): JSX.Element {
	const { t } = useTranslation();
	return (
		<button
			type="button"
			onClick={onClick}
			className="w-full text-left px-4 py-3 text-sm text-neutral-tint-fg hover:bg-brand-input flex items-center gap-3 min-h-touch"
		>
			<FontAwesomeIcon
				icon={icon}
				className="text-base w-5 text-center text-brand-muted"
			/>
			<span className="flex-1">{label}</span>
			{badge != null && badge > 0 && (
				<span className="min-w-[20px] h-5 px-1.5 rounded-full bg-danger text-white text-xs font-semibold flex items-center justify-center leading-none">
					{t(badge > 9 ? '9+' : String(badge))}
				</span>
			)}
		</button>
	);
}
